import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { api, fetchCompanyOptions } from '../lib/api.js';
import { Modal } from './Modal.js';
import { InlinePersonnelForm, Personnel } from './InlinePersonnelForm.js';
import { AsyncSearchableSelect } from './AsyncSearchableSelect.js';
import { generateSmartShortCodeCandidates } from '@gng-ehs/shared';

export interface CompanyOption {
  id: string;
  short_code: string;
  company_name: string;
  occupier_name?: string;
  factory_address?: string;
  license_number?: string;
  registration_number?: string;
  contact_email?: string;
  communication_email?: string;
}

export interface TrainerOption {
  id: string;
  full_name: string;
  designation?: string;
}

export interface ParsedTrainingRow {
  id: string; // internal UI key
  trainee_name: string;
  company_input: string; // raw excel value
  company_id: string; // resolved or user-selected company ID
  course_title: string;
  conducted_by_name: string;
  faculty_input: string; // raw excel value
  faculty1_personnel_id: string; // resolved or selected faculty ID
  training_date: string; // YYYY-MM-DD
  status: 'VALID' | 'MISSING_COMPANY' | 'MISSING_FACULTY' | 'INVALID';
  status_message: string;
  has_multiple_company_matches?: boolean;
  matching_companies_count?: number;
  matching_company_names?: string[];
}

export function generateTrainingExcelTemplate(
  companies: CompanyOption[],
  trainers: TrainerOption[]
) {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Data Entry Template (Clean headers, NO dummy sample rows)
  const templateHeaders = [
    'Trainee Name *',
    'Company (Short Code or Name) *',
    'Course / Training Title *',
    'Faculty / Trainer Name *',
    'Training Date (DD-MM-YYYY) *',
  ];

  const wsData = [templateHeaders];
  const ws1 = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws1['!cols'] = [
    { wch: 28 }, // Trainee Name
    { wch: 40 }, // Company
    { wch: 45 }, // Course Title
    { wch: 32 }, // Trainer Name
    { wch: 26 }, // Training Date
  ];

  // Sheet 2: Lookups & Instructions
  const lookupHeaders = [
    'Existing Company Short Code',
    'Existing Company Full Name',
    ' ',
    'Available Trainer / Faculty Name',
  ];

  const lookupRows: (string | number)[][] = [];
  const maxLen = Math.max(companies.length, trainers.length, 10);
  for (let i = 0; i < maxLen; i++) {
    const c = companies[i];
    const t = trainers[i];
    lookupRows.push([
      c ? c.short_code : '',
      c ? c.company_name : '',
      ' ',
      t ? t.full_name : '',
    ]);
  }

  const instructions = [
    ['INSTRUCTIONS & GUIDE FOR BULK IMPORTING TRAINING CERTIFICATES:'],
    ['1. FILLING EXISTING COMPANY:'],
    ['   - Enter Company Short Code (e.g. SPS) or Company Name in Column B.'],
    ['   - Excel Dropdown: Click cell in Column B to select from available company short codes.'],
    ['2. CREATING NEW COMPANY:'],
    ['   - If company is not in the system yet, type its new name in Column B.'],
    ['   - You can also click "+ New Company" in the upload preview table to create it in 1 click!'],
    ['3. FILLING FACULTY / TRAINER:'],
    ['   - Click cell in Column D to select from the trainer dropdown list.'],
    ['4. TRAINING DATE FORMAT:'],
    ['   - Standard DD-MM-YYYY (e.g. 15-08-2026) or standard Excel date format.'],
    [''],
    ['--- CURRENT SYSTEM LOOKUP DATA ---'],
    lookupHeaders,
    ...lookupRows,
  ];

  const ws2 = XLSX.utils.aoa_to_sheet(instructions);
  ws2['!cols'] = [{ wch: 32 }, { wch: 45 }, { wch: 5 }, { wch: 35 }];

  // SheetJS Data Validation lists for native Excel dropdowns
  const companyCount = Math.max(companies.length, 1);
  const trainerCount = Math.max(trainers.length, 1);

  ws1['!dataValidation'] = [
    {
      sqref: 'B2:B500', // Company Column
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$A$15:$A$${14 + companyCount}`,
      allowBlank: true,
    },
    {
      sqref: 'D2:D500', // Trainer Column
      type: 'list',
      operator: 'equal',
      formula1: `'Lookups'!$D$15:$D$${14 + trainerCount}`,
      allowBlank: true,
    },
  ];

  XLSX.utils.book_append_sheet(wb, ws1, 'Training Certificates Template');
  XLSX.utils.book_append_sheet(wb, ws2, 'Lookups');

  XLSX.writeFile(wb, `Training_Certificates_Import_Template.xlsx`);
}

export function TrainingExcelImportModal({
  open,
  onClose,
  onSuccess,
  defaultHeadPersonnelId = '',
  defaultHeadPersonnelName = '',
  defaultApprovalText = '',
  onOpenGlobalSettings,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultHeadPersonnelId?: string;
  defaultHeadPersonnelName?: string;
  defaultApprovalText?: string;
  onOpenGlobalSettings?: () => void;
}) {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [trainers, setTrainers] = useState<TrainerOption[]>([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [parsedRows, setParsedRows] = useState<ParsedTrainingRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);

  // Quick Add Company panel state (Consistent with single certificate creation)
  const [showQuickCompanyForm, setShowQuickCompanyForm] = useState(false);
  const [activeCompanyTargetRowId, setActiveCompanyTargetRowId] = useState<string | null>(null);
  const [quickCompanyName, setQuickCompanyName] = useState('');
  const [quickCompanySaving, setQuickCompanySaving] = useState(false);

  // Inline Personnel Form state for adding new Faculty
  const [showInlineFacultyForm, setShowInlineFacultyForm] = useState(false);
  const [activeFacultyTargetRowId, setActiveFacultyTargetRowId] = useState<string | null>(null);
  const [activeFacultyInitialName, setActiveFacultyInitialName] = useState<string>('');

  // Batch actions state
  const [batchCompanyId, setBatchCompanyId] = useState('');
  const [batchCourseTitle, setBatchCourseTitle] = useState('');
  const [batchTrainerId, setBatchTrainerId] = useState('');
  const [batchDate, setBatchDate] = useState('');

  // Import execution state
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number; step: string } | null>(null);
  const [error, setError] = useState('');
  const [importSuccessSummary, setImportSuccessSummary] = useState<{ count: number } | null>(null);

  // Load companies & trainers on mount/open
  useEffect(() => {
    if (!open) return;
    setLoadingLookups(true);

    Promise.all([
      api.companies.list({ limit: 1000 }),
      api.personnel.list({ limit: 1000 }),
    ])
      .then(([compRes, persRes]) => {
        const compList = (compRes.data ?? []) as CompanyOption[];
        setCompanies(compList);

        const persList = (persRes.data ?? []) as { id: string; full_name: string; role?: string; designation?: string }[];
        const trainerList = persList.filter((p) => p.role === 'trainer' || p.designation?.toLowerCase().includes('trainer') || p.designation?.toLowerCase().includes('faculty'));
        setTrainers((trainerList.length > 0 ? trainerList : persList) as TrainerOption[]);
      })
      .catch((err) => {
        console.error('Failed to load lookup data:', err);
      })
      .finally(() => setLoadingLookups(false));
  }, [open]);

  // Robust Excel date parser (handles numeric serial codes, ISO, DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, etc.)
  const normalizeDateStr = (raw: unknown): string => {
    if (raw === undefined || raw === null || raw === '') return new Date().toISOString().split('T')[0];

    // Check if raw is a numeric Excel date serial code (e.g. 45519 or "45519")
    const numVal = typeof raw === 'number' ? raw : (typeof raw === 'string' && /^\d{5}(\.\d+)?$/.test(raw.trim()) ? Number(raw.trim()) : NaN);
    if (!isNaN(numVal) && numVal > 25000 && numVal < 75000) {
      const parsedDate = XLSX.SSF.parse_date_code(numVal);
      if (parsedDate) {
        const yyyy = String(parsedDate.y).padStart(4, '0');
        const mm = String(parsedDate.m).padStart(2, '0');
        const dd = String(parsedDate.d).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    // DD/MM/YYYY, DD-MM-YYYY, or DD.MM.YYYY
    const ddmmyyyy = s.match(/^(\d{1,2})[/\-.]( \d{1,2})[/\-.]( \d{4})$/);
    if (ddmmyyyy) {
      const dd = String(ddmmyyyy[1]).padStart(2, '0');
      const mm = String(ddmmyyyy[2]).padStart(2, '0');
      const yyyy = ddmmyyyy[3];
      return `${yyyy}-${mm}-${dd}`;
    }

    // YYYY/MM/DD or YYYY.MM.DD
    const yyyymmdd = s.match(/^(\d{4})[/\-.]( \d{1,2})[/\-.]( \d{1,2})$/);
    if (yyyymmdd) {
      const yyyy = yyyymmdd[1];
      const mm = String(yyyymmdd[2]).padStart(2, '0');
      const dd = String(yyyymmdd[3]).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    // General Date parse (e.g. "15 Aug 2026", "15-Aug-2026")
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    return new Date().toISOString().split('T')[0];
  };

  // Helper entity matcher
  const matchRowData = (
    rawCompany: string,
    rawFaculty: string,
    allCompanies: CompanyOption[],
    allTrainers: TrainerOption[]
  ) => {
    const compClean = rawCompany.trim();
    let company_id = '';
    let has_multiple_company_matches = false;
    let matching_companies_count = 0;
    let matching_company_names: string[] = [];

    if (compClean) {
      const compCleanLower = compClean.toLowerCase();

      // Strong / Direct matches (exact code, exact name, or standard composite formats)
      const exactMatches = allCompanies.filter((c) => {
        const cName = (c.company_name || '').trim().toLowerCase();
        const cCode = (c.short_code || '').trim().toLowerCase();
        return (
          (cCode && cCode === compCleanLower) ||
          (cName && cName === compCleanLower) ||
          (cCode && cName && `${cCode} - ${cName}` === compCleanLower) ||
          (cCode && cName && `${cName} - ${cCode}` === compCleanLower) ||
          (cCode && cName && `${cName} [${cCode}]` === compCleanLower) ||
          (cCode && cName && `${cCode} [${cName}]` === compCleanLower) ||
          (cCode && compCleanLower.startsWith(`${cCode} -`)) ||
          (cName && compCleanLower.startsWith(`${cName} -`)) ||
          (cName && compCleanLower.startsWith(`${cName} [`)) ||
          (cCode && compCleanLower.startsWith(`${cCode} [`))
        );
      });

      let compMatches = exactMatches;

      // Fallback: If no exact/strong match, check substring / contains match
      if (compMatches.length === 0) {
        compMatches = allCompanies.filter((c) => {
          const cName = (c.company_name || '').trim().toLowerCase();
          const cCode = (c.short_code || '').trim().toLowerCase();
          return (
            (cName && (cName.includes(compCleanLower) || (compCleanLower.length >= 3 && compCleanLower.includes(cName)))) ||
            (cCode && cCode.includes(compCleanLower))
          );
        });
      }

      if (compMatches.length > 0) {
        company_id = compMatches[0].id;
        if (compMatches.length > 1) {
          has_multiple_company_matches = true;
          matching_companies_count = compMatches.length;
          matching_company_names = compMatches.map((m) =>
            m.short_code ? `${m.short_code} - ${m.company_name}` : m.company_name
          );
        }
      }
    }

    // Match Faculty / Trainer
    const facClean = rawFaculty.trim();
    let faculty1_personnel_id = '';
    if (facClean) {
      const foundT = allTrainers.find(
        (t) => t.full_name.toLowerCase() === facClean.toLowerCase() || facClean.toLowerCase().includes(t.full_name.toLowerCase())
      );
      if (foundT) {
        faculty1_personnel_id = foundT.id;
      }
    }

    return {
      company_id,
      faculty1_personnel_id,
      has_multiple_company_matches,
      matching_companies_count,
      matching_company_names,
    };
  };

  // File Upload Handler
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    setError('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });

        const sheetName = wb.SheetNames.find(n => n.includes('Template') || n.includes('Certificates') || n.includes('Sheet1')) || wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];

        const rawData = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });

        if (rawData.length === 0) {
          setError('The uploaded Excel file contains no data rows.');
          setParsing(false);
          return;
        }

        const rows: ParsedTrainingRow[] = [];

        rawData.forEach((rowObj, index) => {
          const keys = Object.keys(rowObj);
          const getRawVal = (matches: string[]) => {
            const k = keys.find(key => matches.some(m => key.toLowerCase().trim().includes(m.toLowerCase())));
            return k ? rowObj[k] : undefined;
          };
          const getKeyVal = (matches: string[]) => {
            const val = getRawVal(matches);
            return val !== undefined && val !== null ? String(val).trim() : '';
          };

          const trainee_name = getKeyVal(['trainee name', 'trainee', 'candidate', 'person name', 'name']);
          const company_input = getKeyVal(['company', 'short code', 'organisation', 'client']);
          const course_title = getKeyVal(['course', 'training title', 'title', 'subject']);
          const faculty_input = getKeyVal(['faculty', 'trainer', 'instructor']);
          const rawDate = getRawVal(['training date', 'date']);

          // Skip completely blank rows
          if (!trainee_name && !company_input && rawDate === undefined) return;

          const training_date = normalizeDateStr(rawDate);
          const matched = matchRowData(company_input, faculty_input, companies, trainers);

          let status: 'VALID' | 'MISSING_COMPANY' | 'MISSING_FACULTY' | 'INVALID' = 'VALID';
          let status_message = 'Ready to import';

          if (!trainee_name.trim()) {
            status = 'INVALID';
            status_message = 'Missing Trainee Name';
          } else if (!matched.company_id) {
            status = 'MISSING_COMPANY';
            status_message = company_input ? `Unmatched Company "${company_input}"` : 'Missing Company';
          } else if (!matched.faculty1_personnel_id) {
            status = 'MISSING_FACULTY';
            status_message = faculty_input ? `Unmatched Faculty "${faculty_input}"` : 'Missing Faculty';
          }

          const selectedTrainer = trainers.find((t) => t.id === matched.faculty1_personnel_id);
          const facultyName = selectedTrainer ? selectedTrainer.full_name : faculty_input.trim();

          rows.push({
            id: `row_${index}_${Math.random().toString(36).substring(2, 7)}`,
            trainee_name,
            company_input,
            company_id: matched.company_id,
            course_title,
            conducted_by_name: facultyName,
            faculty_input,
            faculty1_personnel_id: matched.faculty1_personnel_id,
            training_date,
            status,
            status_message,
            has_multiple_company_matches: matched.has_multiple_company_matches,
            matching_companies_count: matched.matching_companies_count,
            matching_company_names: matched.matching_company_names,
          });
        });

        if (rows.length === 0) {
          setError('Could not find valid certificate rows in the file. Please use our downloadable sample Excel template.');
        } else {
          setParsedRows(rows);
        }
      } catch (err: unknown) {
        setError(`Failed to parse Excel file: ${err instanceof Error ? err.message : 'Unknown format'}`);
      } finally {
        setParsing(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  // Row field updates with smart propagation for identical missing strings
  const updateRow = (id: string, updates: Partial<ParsedTrainingRow>) => {
    setParsedRows((prev) => {
      const targetRow = prev.find((r) => r.id === id);
      const targetFacultyInput = targetRow?.faculty_input?.trim().toLowerCase();
      const targetCompanyInput = targetRow?.company_input?.trim().toLowerCase();

      return prev.map((r) => {
        const isTarget = r.id === id;
        const isMatchingSameFaculty =
          !isTarget &&
          updates.faculty1_personnel_id !== undefined &&
          targetFacultyInput &&
          r.faculty_input &&
          r.faculty_input.trim().toLowerCase() === targetFacultyInput;

        const isMatchingSameCompany =
          !isTarget &&
          updates.company_id !== undefined &&
          targetCompanyInput &&
          r.company_input &&
          r.company_input.trim().toLowerCase() === targetCompanyInput;

        if (!isTarget && !isMatchingSameFaculty && !isMatchingSameCompany) {
          return r;
        }

        const updated = {
          ...r,
          ...updates,
          ...(updates.company_id !== undefined ? { has_multiple_company_matches: false } : {}),
        };

        // Re-calculate status
        if (!updated.trainee_name.trim()) {
          updated.status = 'INVALID';
          updated.status_message = 'Missing Trainee Name';
        } else if (!updated.company_id) {
          updated.status = 'MISSING_COMPANY';
          updated.status_message = updated.company_input ? `Unmatched Company "${updated.company_input}"` : 'Missing Company';
        } else if (!updated.course_title.trim()) {
          updated.status = 'INVALID';
          updated.status_message = 'Missing Course Title';
        } else if (!updated.faculty1_personnel_id) {
          updated.status = 'MISSING_FACULTY';
          updated.status_message = updated.faculty_input ? `Unmatched Faculty "${updated.faculty_input}"` : 'Missing Faculty';
        } else {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }
        return updated;
      });
    });
  };

  const removeRow = (id: string) => {
    setParsedRows((prev) => prev.filter((r) => r.id !== id));
  };

  // Batch actions
  const applyBatchCompany = () => {
    if (!batchCompanyId) return;
    const comp = companies.find((c) => c.id === batchCompanyId);
    if (!comp) return;

    setParsedRows((prev) =>
      prev.map((r) => {
        const updated = {
          ...r,
          company_id: comp.id,
          company_input: `${comp.short_code} - ${comp.company_name}`,
        };
        if (updated.trainee_name.trim() && updated.course_title.trim() && updated.faculty1_personnel_id) {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }
        return updated;
      })
    );
  };

  const applyBatchCourseTitle = () => {
    if (!batchCourseTitle.trim()) return;
    setParsedRows((prev) =>
      prev.map((r) => {
        const updated = { ...r, course_title: batchCourseTitle.trim() };
        if (updated.trainee_name.trim() && updated.company_id && updated.faculty1_personnel_id) {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }
        return updated;
      })
    );
  };

  const applyBatchFaculty = () => {
    if (!batchTrainerId) return;
    setParsedRows((prev) =>
      prev.map((r) => {
        const updated = { ...r, faculty1_personnel_id: batchTrainerId };
        if (updated.trainee_name.trim() && updated.company_id && updated.course_title.trim()) {
          updated.status = 'VALID';
          updated.status_message = 'Ready to import';
        }
        return updated;
      })
    );
  };

  const applyBatchDate = () => {
    if (!batchDate) return;
    setParsedRows((prev) =>
      prev.map((r) => ({
        ...r,
        training_date: batchDate,
      }))
    );
  };

  // Quick Add Company Handler (Single Cert Creation Behavior)
  const handleQuickCreateCompany = async () => {
    const name = quickCompanyName.trim();
    if (!name) {
      setError('Please enter a Company Name.');
      return;
    }

    setQuickCompanySaving(true);
    setError('');

    try {
      let short_code = '';
      try {
        const suggestedRes = await api.companies.suggestShortCode(name);
        short_code = suggestedRes.data?.short_code || '';
      } catch {
        /* fallback to client matching */
      }

      if (!short_code) {
        const candidates = generateSmartShortCodeCandidates(name);
        const existingCodes = new Set(companies.map((c) => c.short_code.toUpperCase()));
        short_code = candidates.find((c) => !existingCodes.has(c.toUpperCase())) || candidates[0] || name.slice(0, 4).toUpperCase();
      }

      const createdRes = await api.companies.create({
        company_name: name,
        short_code,
        occupier_name: 'N/A',
        factory_address: 'N/A',
      });

      const newCompany = (createdRes as { data?: CompanyOption }).data;
      if (newCompany?.id) {
        setCompanies((prev) => [...prev, newCompany]);
        setShowQuickCompanyForm(false);

        // Auto-assign to target row and all rows sharing the same unmatched company input
        const targetRow = parsedRows.find((r) => r.id === activeCompanyTargetRowId);
        const targetInput = (name || targetRow?.company_input || '').trim().toLowerCase();

        setParsedRows((prev) =>
          prev.map((r) => {
            const isMatchingRow =
              r.id === activeCompanyTargetRowId ||
              (targetInput && r.company_input && r.company_input.trim().toLowerCase() === targetInput) ||
              (r.company_input && name.toLowerCase().includes(r.company_input.trim().toLowerCase()));

            if (isMatchingRow) {
              const updated = {
                ...r,
                company_id: newCompany.id,
                company_input: `${newCompany.short_code} - ${newCompany.company_name}`,
              };
              if (updated.trainee_name.trim() && updated.course_title.trim() && updated.faculty1_personnel_id) {
                updated.status = 'VALID';
                updated.status_message = 'Ready to import';
              } else if (!updated.faculty1_personnel_id) {
                updated.status = 'MISSING_FACULTY';
                updated.status_message = 'Missing Faculty';
              }
              return updated;
            }
            return r;
          })
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create company');
    } finally {
      setQuickCompanySaving(false);
      setActiveCompanyTargetRowId(null);
      setQuickCompanyName('');
    }
  };

  // Handle Newly Created Faculty from InlinePersonnelForm
  const handleFacultyCreated = (newFaculty: Personnel) => {
    setTrainers((prev) => [...prev, { id: newFaculty.id, full_name: newFaculty.full_name, designation: newFaculty.designation }]);
    setShowInlineFacultyForm(false);

    const targetRow = parsedRows.find((r) => r.id === activeFacultyTargetRowId);
    const targetInput = (activeFacultyInitialName || targetRow?.faculty_input || '').trim().toLowerCase();

    // Auto-resolve for target row and all other rows with matching missing faculty string!
    setParsedRows((prev) =>
      prev.map((r) => {
        const isMatchingRow =
          r.id === activeFacultyTargetRowId ||
          (targetInput && r.faculty_input && r.faculty_input.trim().toLowerCase() === targetInput) ||
          (r.faculty_input && newFaculty.full_name.toLowerCase().includes(r.faculty_input.trim().toLowerCase()));

        if (isMatchingRow) {
          const updated = {
            ...r,
            faculty1_personnel_id: newFaculty.id,
            faculty_input: newFaculty.full_name,
          };
          if (updated.trainee_name.trim() && updated.company_id && updated.course_title.trim()) {
            updated.status = 'VALID';
            updated.status_message = 'Ready to import';
          } else if (!updated.company_id) {
            updated.status = 'MISSING_COMPANY';
            updated.status_message = 'Missing Company';
          }
          return updated;
        }
        return r;
      })
    );

    setActiveFacultyTargetRowId(null);
    setActiveFacultyInitialName('');
  };

  // Count rows with multiple company matches for prominent warning alert banner
  const multipleMatchRowsCount = useMemo(
    () => parsedRows.filter((r) => r.has_multiple_company_matches).length,
    [parsedRows]
  );

  // Check validation rules for Confirm & Import button
  const validationSummary = useMemo(() => {
    if (parsedRows.length === 0) return { isValid: false, reason: 'No rows parsed yet.' };
    if (!defaultHeadPersonnelId) return { isValid: false, reason: 'Head Personnel (Signatory) is required in Global Settings.' };

    const missingNameCount = parsedRows.filter((r) => !r.trainee_name.trim()).length;
    if (missingNameCount > 0) return { isValid: false, reason: `${missingNameCount} row(s) missing Trainee Name.` };

    const missingCompanyCount = parsedRows.filter((r) => !r.company_id).length;
    if (missingCompanyCount > 0) return { isValid: false, reason: `${missingCompanyCount} row(s) missing Company Selection.` };

    const missingCourseCount = parsedRows.filter((r) => !r.course_title.trim()).length;
    if (missingCourseCount > 0) return { isValid: false, reason: `${missingCourseCount} row(s) missing Course Title.` };

    const missingFacultyCount = parsedRows.filter((r) => !r.faculty1_personnel_id).length;
    if (missingFacultyCount > 0) return { isValid: false, reason: `${missingFacultyCount} row(s) missing Faculty / Trainer Selection.` };

    const missingDateCount = parsedRows.filter((r) => !r.training_date).length;
    if (missingDateCount > 0) return { isValid: false, reason: `${missingDateCount} row(s) missing Training Date.` };

    return { isValid: true, reason: 'All mandatory fields satisfied.' };
  }, [parsedRows, defaultHeadPersonnelId]);

  // Bulk Import Execution
  const handleExecuteImport = async () => {
    if (!validationSummary.isValid) {
      setError(validationSummary.reason);
      return;
    }

    setImporting(true);
    setError('');

    try {
      // Create Training Certificates in high-performance batches
      const certPayloads = parsedRows.map((row) => {
        const selectedTrainer = trainers.find((t) => t.id === row.faculty1_personnel_id);
        const facultyName = selectedTrainer ? selectedTrainer.full_name : row.conducted_by_name || row.faculty_input.trim();

        return {
          company_id: row.company_id,
          trainee_name: row.trainee_name.trim(),
          course_title: row.course_title.trim(),
          conducted_by_name: facultyName,
          faculty1_personnel_id: row.faculty1_personnel_id || undefined,
          training_date: row.training_date,
          head_personnel_id: defaultHeadPersonnelId,
          approval_text: defaultApprovalText,
        };
      });

      const BATCH_SIZE = 50;
      let successCount = 0;

      for (let i = 0; i < certPayloads.length; i += BATCH_SIZE) {
        const batch = certPayloads.slice(i, i + BATCH_SIZE);
        const currentCount = Math.min(i + batch.length, certPayloads.length);
        setProgress({
          current: currentCount,
          total: certPayloads.length,
          step: `Importing certificates (${currentCount}/${certPayloads.length})...`,
        });

        await api.training.createBulk({ certificates: batch });
        successCount += batch.length;
      }

      setImportSuccessSummary({ count: successCount });
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Bulk import failed. Please check network/server status.');
    } finally {
      setImporting(false);
      setProgress(null);
    }
  };

  const handleResetModal = () => {
    setParsedRows([]);
    setFileName('');
    setError('');
    setImportSuccessSummary(null);
    setShowInlineFacultyForm(false);
    setShowQuickCompanyForm(false);
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (importing) return;
        handleResetModal();
        onClose();
      }}
      title="🎓 Bulk Import Training Certificates via Excel"
      size="full"
    >
      <div className="space-y-4 text-xs sm:text-sm">
        {/* Top Description & Sample Template Download Button */}
        <div className="p-3.5 bg-brand-50/70 border border-brand-200 rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div>
            <h4 className="font-bold text-brand-900 text-sm">Download Clean Excel Template with Native Dropdowns</h4>
            <p className="text-xs text-brand-700 mt-0.5">
              Download our clean Excel template featuring interactive Excel dropdowns for Companies and Faculty.
            </p>
          </div>
          <button
            type="button"
            onClick={() => generateTrainingExcelTemplate(companies, trainers)}
            disabled={loadingLookups}
            className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 shrink-0 cursor-pointer transition-all"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Download Sample Excel Template
          </button>
        </div>

        {/* Active Global Settings Read-Only Information Card */}
        <div className={`rounded-lg border p-3.5 space-y-2 ${!defaultHeadPersonnelId ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Global Settings (Auto-Applied)</span>
            {onOpenGlobalSettings && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenGlobalSettings();
                }}
                className="text-[11px] text-brand-600 font-medium hover:underline cursor-pointer"
              >
                Edit in Global Settings
              </button>
            )}
          </div>
          <div className="text-xs space-y-1.5">
            <div>
              <span className="font-medium text-slate-600">Head Personnel: </span>
              {defaultHeadPersonnelName ? (
                <span className="font-bold text-slate-900">{defaultHeadPersonnelName}</span>
              ) : (
                <span className="font-bold text-amber-700">⚠️ Not Selected (Required)</span>
              )}
            </div>
            <div>
              <span className="font-medium text-slate-600">Approval Text: </span>
              <p className="text-[11px] text-slate-600 line-clamp-2 font-mono mt-0.5">{defaultApprovalText}</p>
            </div>
          </div>
        </div>

        {!defaultHeadPersonnelId && (
          <div className="rounded-xl bg-amber-50 border border-amber-300 p-3.5 flex items-center justify-between text-xs text-amber-800">
            <span><strong>Head Personnel Required:</strong> You must select a Head Personnel in Global Certificate Settings before importing certificates.</span>
            {onOpenGlobalSettings && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenGlobalSettings();
                }}
                className="ml-2 px-3 py-1.5 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-colors shrink-0 cursor-pointer"
              >
                Configure Now
              </button>
            )}
          </div>
        )}

        {/* Success Modal Screen */}
        {importSuccessSummary ? (
          <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl text-center space-y-4 my-4">
            <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-2xl">
              ✓
            </div>
            <div>
              <h3 className="text-lg font-bold text-emerald-900">Bulk Import Completed Successfully!</h3>
              <p className="text-sm text-emerald-700 mt-1">
                Successfully created <strong>{importSuccessSummary.count} Training Certificates</strong>.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                handleResetModal();
                onClose();
              }}
              className="btn btn-primary text-xs px-6 py-2"
            >
              Close & View Training Certificates
            </button>
          </div>
        ) : (
          <>
            {/* Quick Add Company Panel (Consistent with Single Training Certificate Creation) */}
            {showQuickCompanyForm && (
              <div className="p-4 bg-emerald-50/90 border border-emerald-200 rounded-xl shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-emerald-800">Quick Add Company</p>
                  <button type="button" onClick={() => setShowQuickCompanyForm(false)} className="text-emerald-500 hover:text-emerald-700">✕</button>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="label text-xs font-semibold text-emerald-900">Company Name <span className="text-red-500">*</span></label>
                    {quickCompanyName.trim() && (
                      <span className="text-[11px] font-mono font-semibold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded border border-emerald-200">
                        Auto Code: {generateSmartShortCodeCandidates(quickCompanyName).find((c) => !companies.some((comp) => comp.short_code.toUpperCase() === c.toUpperCase())) || 'NEW'}
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    value={quickCompanyName}
                    onChange={(e) => setQuickCompanyName(e.target.value)}
                    placeholder="Enter Company Name (e.g. Swastik Private Limited)"
                    className="input bg-white text-xs"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setShowQuickCompanyForm(false)} className="btn btn-secondary text-xs">Cancel</button>
                  <button type="button" onClick={handleQuickCreateCompany} disabled={quickCompanySaving} className="btn btn-primary text-xs">
                    {quickCompanySaving ? 'Creating…' : 'Create & Select'}
                  </button>
                </div>
              </div>
            )}

            {/* Inline Personnel Form panel for creating missing Faculty / Trainer */}
            {showInlineFacultyForm && (
              <div className="p-4 bg-brand-50/90 border border-brand-200 rounded-xl shadow-sm space-y-3">
                <InlinePersonnelForm
                  initialRole="trainer"
                  initialFullName={activeFacultyInitialName}
                  title="Create New Faculty / Trainer"
                  onCreated={handleFacultyCreated}
                  onCancel={() => {
                    setShowInlineFacultyForm(false);
                    setActiveFacultyTargetRowId(null);
                    setActiveFacultyInitialName('');
                  }}
                />
              </div>
            )}

            {/* Step 1: Upload File area if no parsed rows yet */}
            {parsedRows.length === 0 ? (
              <div className="border-2 border-dashed border-slate-300 hover:border-brand-500 rounded-2xl p-8 text-center transition-colors bg-white">
                <input
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileUpload}
                  id="excel-file-upload-input"
                  className="hidden"
                />
                <label htmlFor="excel-file-upload-input" className="cursor-pointer block space-y-3">
                  <div className="w-14 h-14 bg-brand-50 text-brand-600 rounded-2xl flex items-center justify-center mx-auto text-2xl shadow-sm">
                    📁
                  </div>
                  <div>
                    <span className="text-base font-bold text-slate-800 hover:text-brand-600 underline">
                      Click to browse & upload Excel file
                    </span>
                    <span className="text-slate-500 text-sm block mt-1">or drag and drop `.xlsx` / `.csv` file here</span>
                  </div>
                  <p className="text-xs text-slate-400">
                    Supports any Excel sheet (matches Company Short Code or Company Name & Trainer Name automatically!)
                  </p>
                </label>
                {parsing && (
                  <p className="text-xs font-semibold text-brand-600 mt-4 flex items-center justify-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-brand-600 border-t-transparent rounded-full" />
                    Parsing Excel spreadsheet data...
                  </p>
                )}
              </div>
            ) : (
              <>
                {/* Step 2: Clean File Info Banner & Sleek Batch Toolbar */}
                <div className="space-y-3">
                  {/* File Info Bar */}
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 bg-brand-100 text-brand-700 rounded-lg flex items-center justify-center font-bold text-xs shrink-0">📄</span>
                      <div>
                        <span className="font-bold text-slate-900 text-sm">
                          Parsed {parsedRows.length} Row(s) from <span className="text-brand-700 font-mono">{fileName}</span>
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleResetModal}
                      className="px-3 py-1 bg-white border border-slate-200 text-slate-600 hover:text-slate-900 rounded-lg text-xs font-semibold hover:bg-slate-50 shadow-sm transition-all"
                    >
                      Upload Different File
                    </button>
                  </div>

                  {/* Batch Actions Toolbar (Sleek 1-Line Flex Layout) */}
                  <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm flex flex-wrap items-center gap-3 text-xs">
                    <span className="font-bold text-slate-800 shrink-0 flex items-center gap-1.5">
                      <span className="text-amber-500 text-sm">⚡</span>
                      <span>Batch Apply:</span>
                    </span>

                    {/* Batch Company with AsyncSearchableSelect & Hover Badge */}
                    <div className="flex items-center gap-1.5 min-w-[240px]">
                      <div className="flex-1">
                        <AsyncSearchableSelect
                          value={batchCompanyId}
                          onChange={(val) => setBatchCompanyId(val)}
                          fetchOptions={fetchCompanyOptions}
                          placeholder="Select Company…"
                          isCompanySelect={true}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={applyBatchCompany}
                        disabled={!batchCompanyId}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply All
                      </button>
                    </div>

                    {/* Batch Course Title */}
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        placeholder="Course Title…"
                        value={batchCourseTitle}
                        onChange={(e) => setBatchCourseTitle(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium w-44"
                      />
                      <button
                        type="button"
                        onClick={applyBatchCourseTitle}
                        disabled={!batchCourseTitle.trim()}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply All
                      </button>
                    </div>

                    {/* Batch Faculty */}
                    <div className="flex items-center gap-1.5">
                      <select
                        value={batchTrainerId}
                        onChange={(e) => setBatchTrainerId(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium max-w-[170px]"
                      >
                        <option value="">-- Faculty --</option>
                        {trainers.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.full_name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={applyBatchFaculty}
                        disabled={!batchTrainerId}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply All
                      </button>
                    </div>

                    {/* Batch Date */}
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        value={batchDate}
                        onChange={(e) => setBatchDate(e.target.value)}
                        className="input text-xs bg-slate-50 border-slate-300 py-1 px-2.5 h-8 font-medium"
                      />
                      <button
                        type="button"
                        onClick={applyBatchDate}
                        disabled={!batchDate}
                        className="px-3 h-8 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-all shadow-sm shrink-0 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
                      >
                        Apply Date
                      </button>
                    </div>
                  </div>
                </div>

                {/* Warning Banner for Multiple Companies matching same name */}
                {multipleMatchRowsCount > 0 && (
                  <div className="p-3.5 bg-amber-50 border border-amber-300 text-amber-950 rounded-xl text-xs flex items-start gap-3 shadow-sm">
                    <span className="text-amber-600 font-bold text-lg shrink-0">⚠️</span>
                    <div className="space-y-1">
                      <h4 className="font-bold text-amber-900 text-xs sm:text-sm">
                        Friendly Note: Multiple Companies Found with Matching Name ({multipleMatchRowsCount} Row{multipleMatchRowsCount > 1 ? 's' : ''})
                      </h4>
                      <p className="text-amber-800 text-[11px] sm:text-xs">
                        The company name in your Excel matches <strong>multiple companies</strong> in the system for {multipleMatchRowsCount} row{multipleMatchRowsCount > 1 ? 's' : ''}.
                        The first match was auto-selected. <strong>Please validate that the selected company is correct before proceeding.</strong>
                      </p>
                    </div>
                  </div>
                )}

                {/* Validation Info / Error Banner */}
                {!validationSummary.isValid && (
                  <div className="p-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-xl text-xs flex items-center justify-between">
                    <span className="font-semibold">⚠️ Action Required: {validationSummary.reason}</span>
                    <span className="text-[11px] text-amber-700">Satisfy all mandatory fields to activate import button</span>
                  </div>
                )}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs flex items-center justify-between">
                    <span>⚠️ {error}</span>
                    <button type="button" onClick={() => setError('')} className="text-red-500 font-bold ml-2">✕</button>
                  </div>
                )}

                {/* Interactive Staging Table */}
                <div className="max-h-[50vh] overflow-y-auto overflow-x-auto border border-slate-200 rounded-xl shadow-inner bg-white">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead className="sticky top-0 bg-slate-100 border-b border-slate-200 z-10 font-bold text-slate-700">
                      <tr>
                        <th className="py-2.5 px-3 w-10 text-center">#</th>
                        <th className="py-2.5 px-3 min-w-[180px]">Trainee Name <span className="text-red-500">*</span></th>
                        <th className="py-2.5 px-3 min-w-[280px]">Company Selection <span className="text-red-500">*</span></th>
                        <th className="py-2.5 px-3 min-w-[200px]">Course Title <span className="text-red-500">*</span></th>
                        <th className="py-2.5 px-3 min-w-[200px]">Faculty / Trainer <span className="text-red-500">*</span></th>
                        <th className="py-2.5 px-3 min-w-[130px]">Training Date <span className="text-red-500">*</span></th>
                        <th className="py-2.5 px-3 min-w-[150px]">Status & Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {parsedRows.map((row, idx) => {
                        return (
                          <tr key={row.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-2 px-3 text-center text-slate-400 font-mono text-[11px]">{idx + 1}</td>

                            {/* Trainee Name */}
                            <td className="py-2 px-3">
                              <input
                                type="text"
                                value={row.trainee_name}
                                onChange={(e) => updateRow(row.id, { trainee_name: e.target.value })}
                                className="input text-xs py-1 px-2 font-semibold bg-white"
                                placeholder="Trainee Full Name"
                                required
                              />
                            </td>

                            {/* Company AsyncSearchableSelect with Hover Badges + + New Company Button */}
                            <td className="py-2 px-3 space-y-1">
                              <div className="flex items-center gap-1">
                                <div className="flex-1">
                                  <AsyncSearchableSelect
                                    value={row.company_id}
                                    onChange={(val) => updateRow(row.id, { company_id: val })}
                                    fetchOptions={fetchCompanyOptions}
                                    placeholder="Search company…"
                                    isCompanySelect={true}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveCompanyTargetRowId(row.id);
                                    setQuickCompanyName(row.company_input || '');
                                    setShowQuickCompanyForm(true);
                                  }}
                                  className="px-1.5 py-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded shrink-0 cursor-pointer"
                                  title="Create New Company"
                                >
                                  + New Company
                                </button>
                              </div>

                              {/* Multiple Company Matches Warning Hint */}
                              {row.has_multiple_company_matches ? (
                                <p className="text-[10px] text-amber-900 font-semibold bg-amber-50/90 p-1.5 rounded border border-amber-300 mt-1">
                                  ⚠️ <strong>Multiple Companies Found ({row.matching_companies_count} matches):</strong> Matched {row.matching_company_names?.join(', ')}. Please validate that this is the intended company.
                                </p>
                              ) : !row.company_id && row.company_input ? (
                                <p className="text-[10px] text-amber-700 font-medium">
                                  Unmatched Excel Company: "{row.company_input}" — Click + New Company to create
                                </p>
                              ) : null}
                            </td>

                            {/* Course Title */}
                            <td className="py-2 px-3">
                              <input
                                type="text"
                                value={row.course_title}
                                onChange={(e) => updateRow(row.id, { course_title: e.target.value })}
                                className="input text-xs py-1 px-2 bg-white"
                                placeholder="Course / Training Title"
                                required
                              />
                            </td>

                            {/* Faculty / Trainer Dropdown + + New Faculty Button */}
                            <td className="py-2 px-3 space-y-1">
                              <div className="flex items-center gap-1">
                                <select
                                  value={row.faculty1_personnel_id}
                                  onChange={(e) => updateRow(row.id, { faculty1_personnel_id: e.target.value })}
                                  className={`input text-xs py-1 px-2 font-medium flex-1 ${!row.faculty1_personnel_id ? 'border-amber-300 bg-amber-50/50' : 'bg-white'}`}
                                >
                                  <option value="">-- Select Trainer --</option>
                                  {trainers.map((t) => (
                                    <option key={t.id} value={t.id}>
                                      {t.full_name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveFacultyTargetRowId(row.id);
                                    setActiveFacultyInitialName(row.faculty_input || '');
                                    setShowInlineFacultyForm(true);
                                  }}
                                  className="px-1.5 py-1 text-[11px] font-bold text-brand-700 hover:text-brand-900 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded shrink-0 cursor-pointer"
                                  title="Create New Faculty"
                                >
                                  + New Faculty
                                </button>
                              </div>
                              {!row.faculty1_personnel_id && row.faculty_input && (
                                <p className="text-[10px] text-amber-700 font-medium">
                                  Unmatched Excel Trainer: "{row.faculty_input}" — Click + New Faculty to create
                                </p>
                              )}
                            </td>

                            {/* Training Date */}
                            <td className="py-2 px-3">
                              <input
                                type="date"
                                value={row.training_date}
                                onChange={(e) => updateRow(row.id, { training_date: e.target.value })}
                                className="input text-xs py-1 px-1.5 bg-white font-medium"
                                required
                              />
                            </td>

                            {/* Status & Actions */}
                            <td className="py-2 px-3">
                              <div className="flex items-center justify-between gap-1 flex-wrap">
                                {row.has_multiple_company_matches && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-300" title="Multiple companies found with this name. Please validate that the correct company is selected.">
                                    ⚠️ Please Validate
                                  </span>
                                )}
                                {row.status === 'VALID' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                    ✓ Ready
                                  </span>
                                )}
                                {row.status === 'MISSING_COMPANY' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                {row.status === 'MISSING_FACULTY' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                {row.status === 'INVALID' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-800" title={row.status_message}>
                                    ⚠️ {row.status_message}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeRow(row.id)}
                                  className="text-slate-400 hover:text-red-600 font-bold p-1"
                                  title="Remove Row"
                                >
                                  ✕
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Progress Bar during import */}
            {importing && progress && (
              <div className="p-4 bg-brand-50 border border-brand-200 rounded-xl space-y-2">
                <div className="flex items-center justify-between text-xs font-bold text-brand-900">
                  <span>{progress.step}</span>
                  <span>{progress.current} / {progress.total}</span>
                </div>
                <div className="w-full bg-brand-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-brand-600 h-2.5 rounded-full transition-all duration-200"
                    style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex justify-between items-center my-2">
                <span>{error}</span>
                <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700 font-bold">✕</button>
              </div>
            )}

            {/* Bottom Modal Actions Bar */}
            <div className="flex items-center justify-between pt-3 border-t border-slate-200">
              <div className="text-xs text-slate-500 font-medium flex items-center gap-2">
                <span>{parsedRows.length > 0 ? `${parsedRows.length} certificate(s) ready` : 'Upload Excel file to begin'}</span>
                {!validationSummary.isValid && parsedRows.length > 0 && (
                  <span className="text-amber-700 font-semibold bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-[11px]">
                    ⚠️ {validationSummary.reason}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    handleResetModal();
                    onClose();
                  }}
                  disabled={importing}
                  className="btn btn-secondary text-xs"
                >
                  Cancel
                </button>
                {parsedRows.length > 0 && (
                  <button
                    type="button"
                    onClick={handleExecuteImport}
                    disabled={importing || !validationSummary.isValid}
                    className={`btn text-xs px-5 py-2 flex items-center gap-1.5 font-semibold transition-all ${
                      validationSummary.isValid && !importing
                        ? 'bg-brand-600 hover:bg-brand-700 text-white shadow-md cursor-pointer'
                        : 'bg-slate-300 text-slate-500 cursor-not-allowed opacity-70'
                    }`}
                    title={!validationSummary.isValid ? validationSummary.reason : 'Import training certificates'}
                  >
                    {importing ? (
                      <>
                        <span className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                        Importing Certificates…
                      </>
                    ) : (
                      <>
                        🚀 Confirm & Import {parsedRows.length} Training Certificates
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
